import React from 'react';
import { Icons } from '../lib/Icons';
import { LegalLayout } from '../components/LegalLayout';

const isSolo = process.env.REACT_APP_SOLO_LANDING === 'true';
const brand = isSolo ? 'SEN-Education' : 'SEN-Ed';
const linkCl = isSolo ? 'text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300' : 'text-indigo-600 hover:text-indigo-800';

const SecurityPage = () => (
  <LegalLayout>
    <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Security</h1>
    <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
      How we protect your data and maintain a secure platform
    </p>

    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 mb-8">
      <div className="flex items-center gap-3 mb-2">
        <Icons.Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
        <h2 className="text-xl font-semibold text-green-900 dark:text-green-200 mt-0 mb-0">Security First</h2>
      </div>
      <p className="text-green-800 dark:text-green-300 mb-0">
        {brand} is built with security by design. We protect sensitive educational data with
        enterprise-grade security measures and comply with UK data protection requirements.
      </p>
    </div>

    <div className="grid md:grid-cols-2 gap-6 mb-12 not-prose">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-6">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isSolo ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-indigo-100 dark:bg-indigo-900/30'}`}>
          <Icons.Lock className={`w-6 h-6 ${isSolo ? 'text-purple-600 dark:text-purple-400' : 'text-indigo-600 dark:text-indigo-400'}`} />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Encryption</h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-6">
        <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-4">
          <Icons.Key className="w-6 h-6 text-purple-600 dark:text-purple-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Authentication</h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          Secure login with optional multi-factor authentication (MFA) for enhanced account protection.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-6">
        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
          <Icons.Server className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">UK/EU Hosting</h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          Data is stored on Google Cloud infrastructure in UK/EU data centres, ensuring data residency compliance.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-6">
        <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
          <Icons.UserGroup className="w-6 h-6 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Access Controls</h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          Role-based access ensures users only see data appropriate to their role within the school.
        </p>
      </div>
    </div>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Infrastructure Security</h2>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Cloud Infrastructure</h3>
      <p className="text-gray-700 dark:text-gray-300">
        {brand} is hosted on Google Firebase and Google Cloud Platform, which provides:
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>SOC 1, SOC 2, and SOC 3 certifications</li>
        <li>ISO 27001 certification</li>
        <li>ISO 27017 (cloud security) and ISO 27018 (cloud privacy)</li>
        <li>Automatic security patches and updates</li>
        <li>DDoS protection</li>
      </ul>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Data Location</h3>
      <p className="text-gray-700 dark:text-gray-300">
        All user data is stored in Google Cloud&apos;s <strong>europe-west2 (London)</strong> and
        <strong> europe-west3 (Frankfurt)</strong> regions, ensuring compliance with UK data
        residency requirements.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Application Security</h2>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Authentication &amp; Access</h3>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Secure authentication:</strong> Powered by Firebase Authentication with secure token management</li>
        <li><strong>Multi-factor authentication:</strong> Available for enhanced security</li>
        <li><strong>Session management:</strong> Automatic session timeout and secure token refresh</li>
        <li><strong>Password security:</strong> Enforced minimum complexity requirements</li>
      </ul>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Data Protection</h3>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Firestore Security Rules:</strong> Database-level access controls ensure data isolation</li>
        <li><strong>Input validation:</strong> All user inputs are sanitised to prevent injection attacks</li>
        <li><strong>HTTPS only:</strong> All communications are encrypted via TLS</li>
        <li><strong>CORS protection:</strong> Cross-origin requests are restricted</li>
      </ul>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Compliance</h2>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">UK GDPR Compliance</h3>
      <p className="text-gray-700 dark:text-gray-300">
        {brand} is designed to comply with UK GDPR requirements:
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>Privacy by design and default</li>
        <li>Data minimisation - we only collect what&apos;s necessary</li>
        <li>Purpose limitation - data is used only for stated purposes</li>
        <li>Subject access request support</li>
        <li>Data Processing Agreements available for schools</li>
      </ul>
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Education Sector Standards</h3>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>Alignment with DfE data protection guidance</li>
        <li>Designed for school safeguarding requirements</li>
        <li>Audit logging for accountability</li>
      </ul>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Payment Security</h2>
      <p className="text-gray-700 dark:text-gray-300">
        All payment processing is handled by <strong>Stripe</strong>, a PCI DSS Level 1 certified payment processor.
        {brand} does not store credit card numbers or sensitive payment details on our servers.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Incident Response</h2>
      <p className="text-gray-700 dark:text-gray-300">
        We have incident response procedures in place to:
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li>Detect and respond to security incidents promptly</li>
        <li>Notify affected users and authorities as required by law</li>
        <li>Conduct post-incident reviews to prevent recurrence</li>
      </ul>
      <p className="text-gray-700 dark:text-gray-300">
        In the event of a data breach affecting personal data, we will notify the ICO within 72 hours
        as required by UK GDPR, and notify affected individuals without undue delay.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">For Schools</h2>
      <p className="text-gray-700 dark:text-gray-300">
        We understand schools have specific security and compliance requirements. We can provide:
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Data Processing Agreement (DPA):</strong> Standard contractual terms for processing student data</li>
        <li><strong>DPIA support:</strong> Information to support your Data Protection Impact Assessments</li>
        <li><strong>Security questionnaire responses:</strong> For your due diligence processes</li>
        <li><strong>Audit logs:</strong> Track user actions for accountability</li>
      </ul>
      <p className="text-gray-700 dark:text-gray-300">
        Contact <a href="mailto:support@sen-ed.co.uk" className={linkCl}>support@sen-ed.co.uk</a> for
        documentation or to discuss your school&apos;s specific requirements.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Reporting Security Issues</h2>
      <p className="text-gray-700 dark:text-gray-300">
        If you discover a security vulnerability, please report it to us responsibly:
      </p>
      <ul className="text-gray-700 dark:text-gray-300">
        <li><strong>Email:</strong> <a href="mailto:support@sen-ed.co.uk" className={linkCl}>support@sen-ed.co.uk</a></li>
      </ul>
      <p className="text-gray-700 dark:text-gray-300">
        Please do not publicly disclose the issue until we have had the opportunity to address it.
        We appreciate responsible disclosure and will acknowledge your contribution.
      </p>
    </section>

    <section className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Questions?</h2>
      <p className="text-gray-700 dark:text-gray-300">
        If you have questions about our security practices, please contact us at{' '}
        <a href="mailto:support@sen-ed.co.uk" className={linkCl}>support@sen-ed.co.uk</a>.
      </p>
    </section>
  </LegalLayout>
);

export default SecurityPage;
